<template>
  <div class="storehouse-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('storehouseManage.page.title')" :subtitle="$t('storehouseManage.page.subtitle')">
      <template #extra>
        <el-button
          v-if="hasPrivilege('502021101638940007')"
          type="primary"
          size="small"
          icon="el-icon-plus"
          @click="openAddStorehouseModal"
        >
          {{ $t('storehouseManage.buttons.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input
            v-model="conditions.shName"
            clearable
            class="w-full"
            :placeholder="$t('storehouseManage.placeholders.shName')"
            @keyup.enter.native="queryStorehouseMethod"
          />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select
            v-model="conditions.isShow"
            clearable
            class="w-full"
            :placeholder="$t('storehouseManage.placeholders.isShow')"
          >
            <el-option :label="$t('storehouseManage.options.all')" value="" />
            <el-option :label="$t('storehouseManage.options.yes')" value="true" />
            <el-option :label="$t('storehouseManage.options.no')" value="false" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input
            v-model="conditions.shId"
            clearable
            class="w-full"
            :placeholder="$t('storehouseManage.placeholders.shId')"
            @keyup.enter.native="queryStorehouseMethod"
          />
        </el-col>
      </el-row>
      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="queryStorehouseMethod">
          {{ $t('storehouseManage.buttons.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="resetStorehouseMethod">
          {{ $t('storehouseManage.buttons.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar
        :total="total"
        :density="density"
        :show-column-setting="false"
        :show-refresh="true"
        @update:density="density = $event"
        @refresh="listStorehouses"
      >
        <template #left>
          <span class="ds-panel__title">{{ $t('storehouseManage.listTitle') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table
        :data="storehouses"
        :loading="loading"
        :error="hasError"
        :error-message="errorMessage"
        :density="density"
        border
        :empty-description="$t('storehouseManage.page.empty')"
        @retry="listStorehouses"
      >
        <el-table-column prop="shId" :label="$t('storehouseManage.table.shId')" align="center" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.shId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="shName" :label="$t('storehouseManage.table.shName')" align="center" min-width="140" />
        <el-table-column :label="$t('storehouseManage.table.isShow')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag
              size="sm"
              :type="scope.row.isShow === 'true' ? 'success' : 'muted'"
              :label="scope.row.isShow === 'true' ? $t('storehouseManage.options.yes') : $t('storehouseManage.options.no')"
            />
          </template>
        </el-table-column>
        <el-table-column :label="$t('storehouseManage.table.allowPurchase')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag
              size="sm"
              :type="scope.row.allowPurchase === 'ON' ? 'success' : 'muted'"
              :label="scope.row.allowPurchase === 'ON' ? $t('storehouseManage.options.yes') : $t('storehouseManage.options.no')"
            />
          </template>
        </el-table-column>
        <el-table-column :label="$t('storehouseManage.table.allowUse')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag
              size="sm"
              :type="scope.row.allowUse === 'ON' ? 'success' : 'muted'"
              :label="scope.row.allowUse === 'ON' ? $t('storehouseManage.options.yes') : $t('storehouseManage.options.no')"
            />
          </template>
        </el-table-column>
        <el-table-column :label="$t('storehouseManage.table.purchaseSwitch')" align="center" min-width="140">
          <template slot-scope="scope">
            <span>{{ scope.row.purchaseSwitch === 'ON' ? scope.row.purchaseFlowName : $t('storehouseManage.options.noAudit') }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('storehouseManage.table.useSwitch')" align="center" min-width="140">
          <template slot-scope="scope">
            <span>{{ scope.row.useSwitch === 'ON' ? scope.row.useFlowName : $t('storehouseManage.options.noAudit') }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('storehouseManage.table.allocationSwitch')" align="center" min-width="140">
          <template slot-scope="scope">
            <span>{{ scope.row.allocationSwitch === 'ON' ? scope.row.allocationFlowName : $t('storehouseManage.options.noAudit') }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('storehouseManage.table.createTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('storehouseManage.table.shDesc')" align="center" min-width="140">
          <template slot-scope="scope">
            <span>{{ scope.row.shDesc && scope.row.shDesc !== '-1' ? scope.row.shDesc : '--' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('storehouseManage.table.operations')" align="center" fixed="right" width="250">
          <template slot-scope="scope">
            <el-button
              v-if="hasPrivilege('502021101197710001')"
              size="mini"
              type="primary"
              @click="openEditStorehouseModel(scope.row)"
            >
              {{ $t('storehouseManage.buttons.edit') }}
            </el-button>
            <el-button
              v-if="hasPrivilege('502021122129210001')"
              size="mini"
              type="danger"
              @click="openDeleteStorehouseModel(scope.row)"
            >
              {{ $t('storehouseManage.buttons.delete') }}
            </el-button>
            <el-button size="mini" @click="openDetailStorehouseModel(scope.row)">
              {{ $t('storehouseManage.buttons.detail') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar
        :page="currentPage"
        :size="pageSize"
        :total="total"
        :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 组件 -->
    <add-storehouse ref="addStorehouse" @success="listStorehouses" />
    <edit-storehouse ref="editStorehouse" @success="listStorehouses" />
    <delete-storehouse ref="deleteStorehouse" @success="listStorehouses" />
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import { listStorehouses } from '@/api/resource/storehouseManageApi'
import AddStorehouse from '@/components/resource/AddStorehouse'
import EditStorehouse from '@/components/resource/EditStorehouse'
import DeleteStorehouse from '@/components/resource/DeleteStorehouse'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'StorehouseManageList',
  components: {
    AddStorehouse,
    EditStorehouse,
    DeleteStorehouse,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      currentPage: 1,
      pageSize: 10,
      total: 0,
      conditions: {
        shName: '',
        isShow: '',
        shId: '',
        communityId: ''
      },
      storehouses: []
    }
  },
  created() {
    this.conditions.communityId = getCommunityId()
    this.listStorehouses()
  },
  methods: {
    async listStorehouses() {
      this.loading = true
      this.hasError = false
      try {
        const params = {
          page: this.currentPage,
          row: this.pageSize,
          ...this.conditions
        }
        const response = await listStorehouses(params)
        this.storehouses = response.data
        this.total = response.total
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('storehouseManage.messages.fetchError')
        this.$message.error(this.$t('storehouseManage.messages.fetchError'))
      } finally {
        this.loading = false
      }
    },
    queryStorehouseMethod() {
      this.currentPage = 1
      this.listStorehouses()
    },
    resetStorehouseMethod() {
      this.conditions = {
        shName: '',
        isShow: '',
        shId: '',
        communityId: getCommunityId()
      }
      this.queryStorehouseMethod()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.listStorehouses()
    },
    handleCurrentChange(page) {
      this.currentPage = page
      this.listStorehouses()
    },
    openAddStorehouseModal() {
      this.$refs.addStorehouse.open()
    },
    openEditStorehouseModel(row) {
      this.$refs.editStorehouse.open(row)
    },
    openDeleteStorehouseModel(row) {
      this.$refs.deleteStorehouse.open(row)
    },
    openDetailStorehouseModel(row) {
      this.$router.push(`/pages/common/resourceStoreManage?shId=${row.shId}&flag=1`)
    },
  }
}
</script>

<style lang="scss" scoped>
.storehouse-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.w-full {
  width: 100%;
}
</style>
